import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { playNotificationSound } from '../utils/notificationSound';

const COLORS = {
  assigned: '#8B5CF6',
  accepted: '#3B82F6',
  on_the_way_to_store: '#3B82F6',
  arrived_at_store: '#F59E0B',
  picked_up_from_store: '#F97316',
  on_the_way_to_customer: '#9c27b0',
  arrived_at_customer: '#10B981',
  delivered: '#3E2C6E',
  not_delivered: '#ff9800',
  returning_to_store: '#ff6f00',
  returned_at_store: '#607d8b',
  delivery_failed: '#d32f2f',
  cancelled: '#f44336',
};

function STATUS_BG(s) { return COLORS[s] || '#3E2C6E'; }

export default function LiveToast({ lang = 'ar' }) {
  const [toast, setToast] = useState(null);
  const timerRef = useRef(null);

  const show = useCallback((data) => {
    if (!data) return;
    const hasOrderStatus = data.status && data.order_number;
    const hasNotifText = (data.title && String(data.title).trim()) || (data.body && String(data.body).trim());
    if (!hasOrderStatus && !hasNotifText) return;

    if (timerRef.current) clearTimeout(timerRef.current);
    playNotificationSound();
    setToast(data);
    timerRef.current = setTimeout(() => setToast(null), 4000);
  }, []);

  useEffect(() => {
    const handler = (e) => show(e.detail);
    window.addEventListener('order_status_update', handler);
    window.addEventListener('app_notification', handler);
    return () => {
      window.removeEventListener('order_status_update', handler);
      window.removeEventListener('app_notification', handler);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [show]);

  if (!toast) return null;

  const statusAr = {
    assigned: 'تم الإسناد', accepted: 'قبول الطلب',
    delivered: 'تم التوصيل', cancelled: 'ملغي',
    delivery_failed: 'تعذر التوصيل', not_delivered: 'لم يتم التوصيل',
  };

  const isOrderStatus = toast.status && toast.order_number;
  const headline = isOrderStatus
    ? (toast.order_number || '')
    : (toast.title || '');
  const subline = isOrderStatus
    ? `${toast.rider_name ? `${toast.rider_name} — ` : ''}${lang === 'ar' ? (statusAr[toast.status] || toast.status) : toast.status}`
    : (toast.body || '');

  return createPortal(
    <div style={{
      position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)',
      zIndex: 10050, direction: lang === 'ar' ? 'rtl' : 'ltr',
      animation: 'slideUpToast 0.3s ease-out',
      maxWidth: 380, width: 'min(90vw, 380px)',
      pointerEvents: 'auto',
    }}>
      <div style={{
        background: '#fff', borderRadius: 16, padding: '14px 18px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
        borderRight: `4px solid ${STATUS_BG(toast.status || 'assigned')}`,
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <div style={{
          width: 36, height: 36, borderRadius: '50%',
          background: STATUS_BG(toast.status || 'assigned'),
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontSize: 18, flexShrink: 0,
        }}>
          {toast.status === 'delivered' ? '✓' : toast.status === 'cancelled' ? '✕' : '🔔'}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#333', marginBottom: 2 }}>
            {headline}
          </div>
          {subline ? (
            <div style={{ fontSize: 12, color: '#666', lineHeight: 1.4 }}>
              {subline}
            </div>
          ) : null}
        </div>
        <button onClick={() => setToast(null)} style={{
          background: 'none', border: 'none', fontSize: 18, color: '#999',
          cursor: 'pointer', padding: 4, lineHeight: 1,
        }}>×</button>
      </div>
      <style>{`
        @keyframes slideUpToast {
          from { opacity: 0; transform: translateX(-50%) translateY(20px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
    </div>,
    document.body
  );
}
